import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Shield, Clock, Handshake } from "lucide-react";
import { Reveal, LineReveal } from "@/components/Reveal";
import Counter from "@/components/Counter";
import { IMAGES, TEAM, STATS, CONTACT } from "@/lib/data";
const VALUES = [
  { icon: Shield, title: "Unabhängig", text: "Wir arbeiten ausschließlich in Ihrem Interesse!" },
  { icon: Award, title: "Zertifiziert", text: "BVSK und seit 2002 Vertragspartner der Classic Data GmbH. GTÜ Vorvertragspartner. DAT-Expert-Partner." },
  { icon: Clock, title: "Schnell", text: "Ihre Anfrage beantworten wir in der Regel innerhalb weniger Stunden." },
  { icon: Handshake, title: "Persönlich", text: "Wir hören zu, verstehen Ihr Fahrzeug und dessen Geschichte." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[color:var(--cream)]">
      <section className="pt-40 lg:pt-48 pb-24 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-6">— Über uns</div>
            <LineReveal
              className="display-xl text-6xl md:text-7xl lg:text-[7.5vw] leading-[0.92]"
              lines={["Präzision.", "Erfahrung.", "Und die Liebe zum"]}
            />
            <LineReveal className="display-xl italic text-6xl md:text-7xl lg:text-[7.5vw] leading-[0.92]" lines={["klassischen Automobil."]} delay={0.3} />
          </div>
          <Reveal className="lg:col-span-4" delay={0.5}>
            <p className="text-[color:var(--ink-2)] leading-relaxed">
              Das KFZ-Sachverständigenbüro Björn Linkowitz ist seit fast drei Jahrzehnten in Berlin und Brandenburg zuhause. Wir sind ein kleines, spezialisiertes Team — mit dem klaren Auftrag, Klassiker fair zu bewerten.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-8">
            <div className="relative overflow-hidden aspect-[16/10] bg-black">
              <img src={IMAGES.work3} alt="Am Schreibtisch" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={0.1}>
            <div className="relative overflow-hidden aspect-[3/4] bg-black h-full">
              <img src={IMAGES.expertCadillac} alt="Vor Ort" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-32 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <div className="eyebrow mb-6">— Werte</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">Vier Prinzipien, <em className="italic">die uns leiten.</em></h2>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="border border-black/10 p-8 h-full">
                  <v.icon className="h-6 w-6 text-[color:var(--burgundy)]" />
                  <h3 className="font-serif text-2xl mt-4">{v.title}</h3>
                  <p className="mt-3 text-sm text-[color:var(--ink-2)] leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-32 px-6 lg:px-10 bg-[color:var(--cream-2)]">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="eyebrow mb-6">— Team</div>
            <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] max-w-3xl">Menschen, denen Sie <em className="italic">Ihren Klassiker</em> anvertrauen können.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.1}>
                <div className="group">
                  <div className="relative overflow-hidden aspect-[3/4] bg-black">
                    <img src={IMAGES[m.img]} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms]" />
                  </div>
                  <div className="mt-5">
                    <div className="font-serif text-2xl">{m.name}</div>
                    <div className="text-xs tracking-[0.14em] uppercase text-[color:var(--burgundy)] mt-2">{m.role}</div>
                    <div className="text-sm text-[color:var(--ink-2)] mt-3 leading-relaxed">{m.note}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 px-6 lg:px-10 bg-[color:var(--ink)] text-[color:var(--cream)]">
        <div className="mx-auto max-w-[1400px] grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s) => (
            <Reveal key={s.label}>
              <div className="border-t border-white/25 pt-6">
                <div className="font-serif text-6xl lg:text-7xl leading-none"><Counter to={s.value} suffix={s.suffix} /></div>
                <div className="mt-4 text-sm">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-t border-black/15 pt-10">
          <div className="font-serif text-3xl md:text-4xl max-w-xl">Wir freuen uns auf Ihren <em className="italic">Klassiker.</em></div>
          <Link to="/kontakt" className="btn-primary" data-testid="about-cta">Kontakt aufnehmen <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
}
